import config from "config";
import { refreshAuthSession } from "services/auth-session.service";
import authNetworkService from "services/sub-services/auth-network-service/auth-network.service";
import networkService from "services/sub-services/network-service/network.service";
import {
  AdminUserSummary,
  CurrentSessionResponse,
  NewsArticleSummary,
  SessionListResponse,
} from "types/api";

const apiBaseUrl = config.apiControlUrl();

type ApiPayload = Record<string, unknown>;

const compactObject = <T extends ApiPayload>(value: T) =>
  Object.fromEntries(Object.entries(value).filter(([, current]) => current !== undefined));

const countryPath = (name: string) => encodeURIComponent(name.trim().replace(/\s+/g, "_"));
const mediaFieldNames = new Set([
  "image",
  "image_url",
  "profile_image_url",
  "user_image_url",
  "avatar",
  "avatar_url",
  "media_url",
  "file_url",
  "thumbnail",
  "thumbnail_url",
  "banner",
  "banner_url",
  "logo",
  "logo_url",
  "sport_image_url",
  "school_image_url",
  "school_team_image_url",
  "home_school_team_image_url",
  "away_school_team_image_url",
  "home_school_image_url",
  "away_school_image_url",
]);
const mediaArrayFieldNames = new Set(["gallery_images"]);

const isFileLike = (value: unknown): value is File =>
  typeof File !== "undefined" && value instanceof File;

const isAbsoluteUrl = (value: string) =>
  /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ||
  value.startsWith("//") ||
  value.startsWith("data:") ||
  value.startsWith("blob:");

const withHostUrl = (path: string) => {
  const hostUrl = config.hostUrl?.trim();

  if (!hostUrl) {
    return path;
  }

  return `${hostUrl.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
};

const normalizeMediaUrl = (value: string) => {
  const trimmed = value.trim();

  if (!trimmed || isAbsoluteUrl(trimmed)) {
    return value;
  }

  if (trimmed.startsWith("/") || /^(rails|uploads|storage|active_storage)\b/i.test(trimmed)) {
    return withHostUrl(trimmed);
  }

  return value;
};

const hasFileValue = (value: unknown): boolean => {
  if (isFileLike(value)) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some(hasFileValue);
  }

  if (value && typeof value === "object") {
    return Object.values(value as Record<string, unknown>).some(hasFileValue);
  }

  return false;
};

const appendFormValue = (formData: FormData, key: string, value: unknown) => {
  if (value === undefined || value === null) {
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((entry) => appendFormValue(formData, `${key}[]`, entry));
    return;
  }

  if (isFileLike(value)) {
    formData.append(key, value);
    return;
  }

  if (value instanceof Date) {
    formData.append(key, value.toISOString());
    return;
  }

  if (typeof value === "object") {
    Object.entries(value as Record<string, unknown>).forEach(([nestedKey, nestedValue]) => {
      appendFormValue(formData, `${key}[${nestedKey}]`, nestedValue);
    });
    return;
  }

  formData.append(key, String(value));
};

const buildNestedFormData = (rootKey: string, payload: ApiPayload) => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) => {
    appendFormValue(formData, `${rootKey}[${key}]`, value);
  });

  return formData;
};

const normalizeApiPayload = <T>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeApiPayload(entry)) as T;
  }

  if (
    !value ||
    typeof value !== "object" ||
    isFileLike(value) ||
    value instanceof Date ||
    value instanceof FormData
  ) {
    return value;
  }

  const normalized = Object.fromEntries(
    Object.entries(value).map(([key, entry]) => {
      if (mediaArrayFieldNames.has(key) && Array.isArray(entry)) {
        return [
          key,
          entry.map((item) =>
            typeof item === "string" ? normalizeMediaUrl(item) : normalizeApiPayload(item),
          ),
        ];
      }

      if (mediaFieldNames.has(key) && typeof entry === "string") {
        return [key, normalizeMediaUrl(entry)];
      }

      return [key, normalizeApiPayload(entry)];
    }),
  ) as ApiPayload;

  if (typeof normalized.image === "string" && !normalized.image_url) {
    normalized.image_url = normalized.image;
  }

  if (typeof normalized.image_url === "string" && !normalized.image) {
    normalized.image = normalized.image_url;
  }

  if (typeof normalized.avatar === "string" && !normalized.avatar_url) {
    normalized.avatar_url = normalized.avatar;
  }

  if (typeof normalized.avatar_url === "string" && !normalized.avatar) {
    normalized.avatar = normalized.avatar_url;
  }

  return normalized as T;
};

const getResponseData = <T>(response: { data: T }) => normalizeApiPayload(response.data);
const getCurrentUserResponseData = <T extends { user?: unknown }>(response: { data: T }) =>
  normalizeApiPayload((response.data?.user || response.data) as AdminUserSummary);

const authMultipartRequest = async ({
  method,
  path,
  rootKey,
  payload,
}: {
  method: "post" | "patch" | "put";
  path: string;
  rootKey: string;
  payload: ApiPayload;
}) => {
  const formData = buildNestedFormData(rootKey, payload);

  const response = await authNetworkService.request({
    method,
    url: path,
    data: formData,
    headers: {
      Accept: "application/json",
    },
  });

  return getResponseData(response);
};

const authJsonRequest = async ({
  method,
  path,
  payload,
  rootKey,
}: {
  method: "post" | "patch" | "put";
  path: string;
  payload?: ApiPayload;
  rootKey?: string;
}) => {
  const response = await authNetworkService.request({
    method,
    url: path,
    data: rootKey && payload ? { [rootKey]: payload } : payload,
  });

  return getResponseData(response);
};

const adminApi = {
  login: async (payload: { email: string; password: string }) => {
    const response = await networkService.post(`${apiBaseUrl}/auth/session`, payload);
    return getResponseData(response);
  },

  sessions: {
    current: async () => {
      const response = await authNetworkService.get(`${apiBaseUrl}/auth/session`);
      return getResponseData<CurrentSessionResponse>(response);
    },
    list: async () => {
      const response = await authNetworkService.get(`${apiBaseUrl}/auth/sessions`);
      return getResponseData<SessionListResponse>(response);
    },
    logoutCurrent: async () => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/auth/session`);
      return getResponseData(response);
    },
    remove: async (sessionId: string | number) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/auth/sessions/${sessionId}`);
      return getResponseData(response);
    },
    logoutAll: async () => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/auth/sessions`);
      return getResponseData(response);
    },
    refreshCurrent: async () => {
      const response = await refreshAuthSession();
      return getResponseData(response);
    },
  },

  dashboard: async () => {
    const response = await authNetworkService.get(`${apiBaseUrl}/admin/dashboard`);
    return getResponseData(response);
  },

  currentUser: async () => {
    const response = await authNetworkService.get(`${apiBaseUrl}/users/me`);
    return getCurrentUserResponseData(response);
  },
  updateCurrentUser: async (payload: Record<string, any>) => {
    const normalizedPayload = compactObject(payload);

    if (hasFileValue(normalizedPayload)) {
      return authMultipartRequest({
        method: "put",
        path: `${apiBaseUrl}/users/me`,
        rootKey: "user",
        payload: normalizedPayload,
      });
    }

    return authJsonRequest({
      method: "put",
      path: `${apiBaseUrl}/users/me`,
      rootKey: "user",
      payload: normalizedPayload,
    });
  },

  sports: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/sports`, { params });
      return getResponseData(response);
    },
    get: async (id: string) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/sports/${id}`);
      return getResponseData(response);
    },
    getTeams: async (id: string, params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/sports/${id}/teams`, { params });
      return getResponseData(response);
    },
    getAthletes: async (id: string, params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/sports/${id}/athletes`, {
        params,
      });
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/sports`,
          rootKey: "sport",
          payload: normalizedPayload,
        });
      }

      const response = await authNetworkService.post(`${apiBaseUrl}/sports`, {
        sport: normalizedPayload,
      });
      return getResponseData(response);
    },
    update: async (id: string, payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "patch",
          path: `${apiBaseUrl}/sports/${id}`,
          rootKey: "sport",
          payload: normalizedPayload,
        });
      }

      const response = await authNetworkService.patch(`${apiBaseUrl}/sports/${id}`, {
        sport: normalizedPayload,
      });
      return getResponseData(response);
    },
    remove: async (id: string) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/sports/${id}`);
      return getResponseData(response);
    },
  },

  teams: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/teams`, { params });
      return getResponseData(response);
    },
    get: async (id: string) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/teams/${id}`);
      return getResponseData(response);
    },
    getPlayers: async (id: string, params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/teams/${id}/players`, {
        params,
      });
      return getResponseData(response);
    },
    searchApiSports: async (params: { q: string; sport?: string }) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/admin/teams/api_sports_search`, {
        params: compactObject(params),
      });
      return getResponseData(response);
    },
    importApiSports: async (payload: Record<string, any>) => {
      const response = await authNetworkService.post(
        `${apiBaseUrl}/admin/teams/import_api_sports`,
        compactObject(payload),
      );
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/teams`,
          rootKey: "team",
          payload: normalizedPayload,
        });
      }

      const response = await authNetworkService.post(`${apiBaseUrl}/teams`, {
        team: normalizedPayload,
      });
      return getResponseData(response);
    },
    update: async (id: string, payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "patch",
          path: `${apiBaseUrl}/teams/${id}`,
          rootKey: "team",
          payload: normalizedPayload,
        });
      }

      const response = await authNetworkService.patch(`${apiBaseUrl}/teams/${id}`, {
        team: normalizedPayload,
      });
      return getResponseData(response);
    },
    remove: async (id: string) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/teams/${id}`);
      return getResponseData(response);
    },
  },

  athletes: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/players`, { params });
      return getResponseData(response);
    },
    get: async (id: string) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/players/${id}`);
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) => {
      const response = await authNetworkService.post(`${apiBaseUrl}/players`, {
        player: compactObject(payload),
      });
      return getResponseData(response);
    },
    update: async (id: string, payload: Record<string, any>) => {
      const response = await authNetworkService.patch(`${apiBaseUrl}/players/${id}`, {
        player: compactObject(payload),
      });
      return getResponseData(response);
    },
    getCalendarEvents: async (id: string, params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/players/${id}/calendar_events`, {
        params,
      });
      return getResponseData(response);
    },
    getCareers: async (id: string) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/players/${id}/player_careers`);
      return getResponseData(response);
    },
    getSeasonStats: async (id: string) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/players/${id}/player_season_stats`,
      );
      return getResponseData(response);
    },
    getPositionsPlayed: async (id: string) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/players/${id}/player_positions_played`,
      );
      return getResponseData(response);
    },
    getAchievements: async (id: string) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/players/${id}/player_achievements`,
      );
      return getResponseData(response);
    },
    remove: async (id: string) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/admin/players/${id}`);
      return getResponseData(response);
    },
  },

  playerCareers: {
    create: async (playerId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/players/${playerId}/player_careers`,
        rootKey: "player_career",
        payload: compactObject(payload),
      }),
    update: async (playerId: string | number, id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/players/${playerId}/player_careers/${id}`,
        rootKey: "player_career",
        payload: compactObject(payload),
      }),
    remove: async (playerId: string | number, id: string | number) => {
      const response = await authNetworkService.delete(
        `${apiBaseUrl}/players/${playerId}/player_careers/${id}`,
      );
      return getResponseData(response);
    },
  },

  playerSeasonStats: {
    create: async (playerId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/players/${playerId}/player_season_stats`,
        rootKey: "player_season_stat",
        payload: compactObject(payload),
      }),
    update: async (playerId: string | number, id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/players/${playerId}/player_season_stats/${id}`,
        rootKey: "player_season_stat",
        payload: compactObject(payload),
      }),
    remove: async (playerId: string | number, id: string | number) => {
      const response = await authNetworkService.delete(
        `${apiBaseUrl}/players/${playerId}/player_season_stats/${id}`,
      );
      return getResponseData(response);
    },
  },

  playerPositionsPlayed: {
    create: async (playerId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/players/${playerId}/player_positions_played`,
        rootKey: "player_position_played",
        payload: compactObject(payload),
      }),
    update: async (playerId: string | number, id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/players/${playerId}/player_positions_played/${id}`,
        rootKey: "player_position_played",
        payload: compactObject(payload),
      }),
    remove: async (playerId: string | number, id: string | number) => {
      const response = await authNetworkService.delete(
        `${apiBaseUrl}/players/${playerId}/player_positions_played/${id}`,
      );
      return getResponseData(response);
    },
  },

  playerAchievements: {
    create: async (playerId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/players/${playerId}/player_achievements`,
        rootKey: "player_achievement",
        payload: compactObject(payload),
      }),
    update: async (playerId: string | number, id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/players/${playerId}/player_achievements/${id}`,
        rootKey: "player_achievement",
        payload: compactObject(payload),
      }),
    remove: async (playerId: string | number, id: string | number) => {
      const response = await authNetworkService.delete(
        `${apiBaseUrl}/players/${playerId}/player_achievements/${id}`,
      );
      return getResponseData(response);
    },
  },

  users: {
    list: async () => {
      const response = await authNetworkService.get(`${apiBaseUrl}/users`);
      return getResponseData(response);
    },
    listScoped: async (schoolId: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/schools/${schoolId}/users`);
      return getResponseData(response);
    },
    get: async (id: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/users/${id}`);
      return getResponseData(response);
    },
    getScoped: async (schoolId: string | number, id: string | number) => {
      // TODO(api): Scoped/public staff user GETs should omit wallet, orders, and payments.
      // Frontend no longer renders those on school staff profiles, but the payload may still
      // include them until the backend strips sensitive commerce from this endpoint.
      try {
        const response = await authNetworkService.get(
          `${apiBaseUrl}/schools/${schoolId}/users/${id}`,
        );
        return getResponseData(response);
      } catch (error: any) {
        if ([403, 404].includes(error?.response?.status)) {
          const response = await authNetworkService.get(`${apiBaseUrl}/users/${id}`);
          return getResponseData(response);
        }

        throw error;
      }
    },
    create: async (payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/users`,
          rootKey: "user",
          payload: normalizedPayload,
        });
      }

      const response = await authNetworkService.post(`${apiBaseUrl}/users`, {
        user: normalizedPayload,
      });
      return getResponseData(response);
    },
    replaceTemporaryAdmin: async (payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/users/replace_temporary_admin`,
          rootKey: "user",
          payload: normalizedPayload,
        });
      }

      return authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/users/replace_temporary_admin`,
        rootKey: "user",
        payload: normalizedPayload,
      });
    },
    update: async (id: string | number, payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "put",
          path: `${apiBaseUrl}/users/${id}`,
          rootKey: "user",
          payload: normalizedPayload,
        });
      }

      const response = await authNetworkService.put(`${apiBaseUrl}/users/${id}`, {
        user: normalizedPayload,
      });
      return getResponseData(response);
    },
    followedAthletes: async (id?: string | number, params?: Record<string, any>) => {
      const target = id
        ? `${apiBaseUrl}/users/${id}/followed_athletes`
        : `${apiBaseUrl}/users/followed_athletes`;
      const response = await authNetworkService.get(target, { params });
      return getResponseData(response);
    },
    followAthlete: async (id: string | number, playerId: string | number) => {
      const response = await authNetworkService.post(`${apiBaseUrl}/users/${id}/follow_player`, {
        player_id: playerId,
      });
      return getResponseData(response);
    },
    unfollowAthlete: async (id: string | number, playerId: string | number) => {
      const response = await authNetworkService.post(`${apiBaseUrl}/users/${id}/unfollow_player`, {
        player_id: playerId,
      });
      return getResponseData(response);
    },
  },

  countries: {
    list: async () => {
      const response = await authNetworkService.get(`${apiBaseUrl}/countries`);
      return getResponseData(response);
    },
    create: async (name: string) => {
      const response = await authNetworkService.post(`${apiBaseUrl}/countries`, {
        country: { name },
      });
      return getResponseData(response);
    },
    update: async (currentName: string, nextName: string) => {
      const response = await authNetworkService.put(
        `${apiBaseUrl}/countries/${countryPath(currentName)}`,
        {
          country: { name: nextName },
        },
      );
      return getResponseData(response);
    },
    remove: async (name: string) => {
      const response = await authNetworkService.delete(
        `${apiBaseUrl}/countries/${countryPath(name)}`,
      );
      return getResponseData(response);
    },
  },

  administrativeRegions: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/administrative_regions`, {
        params,
      });
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/administrative_regions`,
        rootKey: "administrative_region",
        payload: compactObject(payload),
      }),
    update: async (id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/administrative_regions/${id}`,
        rootKey: "administrative_region",
        payload: compactObject(payload),
      }),
    remove: async (id: string | number) => {
      const response = await authNetworkService.delete(
        `${apiBaseUrl}/administrative_regions/${id}`,
      );
      return getResponseData(response);
    },
  },

  sportAgeGroups: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/sport_age_groups`, { params });
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/sport_age_groups`,
        rootKey: "sport_age_group",
        payload: compactObject(payload),
      }),
    update: async (id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/sport_age_groups/${id}`,
        rootKey: "sport_age_group",
        payload: compactObject(payload),
      }),
    remove: async (id: string | number) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/sport_age_groups/${id}`);
      return getResponseData(response);
    },
  },

  schools: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/schools`, { params });
      return getResponseData(response);
    },
    get: async (id: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/schools/${id}`);
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) => {
      if (hasFileValue(payload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/schools`,
          rootKey: "school",
          payload,
        });
      }

      return authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/schools`,
        rootKey: "school",
        payload,
      });
    },
    update: async (id: string | number, payload: Record<string, any>) => {
      if (hasFileValue(payload)) {
        return authMultipartRequest({
          method: "put",
          path: `${apiBaseUrl}/schools/${id}`,
          rootKey: "school",
          payload,
        });
      }

      return authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/schools/${id}`,
        rootKey: "school",
        payload,
      });
    },
  },

  schoolDashboards: {
    get: async (schoolId: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/schools/${schoolId}/dashboard`);
      return getResponseData(response);
    },
  },

  schoolRoles: {
    list: async (schoolId: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/schools/${schoolId}/roles`);
      return getResponseData(response);
    },
    listCandidates: async (schoolId: string | number, params?: Record<string, any>) => {
      try {
        const response = await authNetworkService.get(
          `${apiBaseUrl}/schools/${schoolId}/role_candidates`,
          {
            params,
          },
        );
        return getResponseData(response);
      } catch (error: any) {
        if ([400, 403, 404, 422].includes(error?.response?.status)) {
          const response = await authNetworkService.get(`${apiBaseUrl}/schools/${schoolId}/users`, {
            params,
          });
          return getResponseData(response);
        }

        throw error;
      }
    },
    create: async (schoolId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/schools/${schoolId}/roles`,
        rootKey: "school_role_assignment",
        payload: compactObject(payload),
      }),
    update: async (schoolId: string | number, id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/schools/${schoolId}/roles/${id}`,
        rootKey: "school_role_assignment",
        payload: compactObject(payload),
      }),
    remove: async (schoolId: string | number, id: string | number) => {
      const response = await authNetworkService.delete(
        `${apiBaseUrl}/schools/${schoolId}/roles/${id}`,
      );
      return getResponseData(response);
    },
  },

  schoolSports: {
    list: async (schoolId: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/schools/${schoolId}/school_sports`,
      );
      return getResponseData(response);
    },
    create: async (schoolId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/schools/${schoolId}/school_sports`,
        rootKey: "school_sport",
        payload: compactObject(payload),
      }),
    update: async (schoolId: string | number, id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/schools/${schoolId}/school_sports/${id}`,
        rootKey: "school_sport",
        payload: compactObject(payload),
      }),
  },

  schoolTeams: {
    list: async (schoolId: string | number, params?: Record<string, any>) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/schools/${schoolId}/school_teams`,
        {
          params,
        },
      );
      return getResponseData(response);
    },
    get: async (id: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/school_teams/${id}`);
      return getResponseData(response);
    },
    create: async (schoolId: string | number, payload: Record<string, any>) => {
      if (hasFileValue(payload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/schools/${schoolId}/school_teams`,
          rootKey: "school_team",
          payload,
        });
      }

      return authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/schools/${schoolId}/school_teams`,
        rootKey: "school_team",
        payload,
      });
    },
    update: async (id: string | number, payload: Record<string, any>) => {
      if (hasFileValue(payload)) {
        return authMultipartRequest({
          method: "put",
          path: `${apiBaseUrl}/school_teams/${id}`,
          rootKey: "school_team",
          payload,
        });
      }

      return authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/school_teams/${id}`,
        rootKey: "school_team",
        payload,
      });
    },
    remove: async (id: string | number) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/school_teams/${id}`);
      return getResponseData(response);
    },
  },

  schoolTeamDashboards: {
    get: async (schoolTeamId: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/school_teams/${schoolTeamId}/dashboard`,
      );
      return getResponseData(response);
    },
  },

  schoolPlayers: {
    list: async (schoolId: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/schools/${schoolId}/players`);
      return getResponseData(response);
    },
    get: async (schoolId: string | number, id: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/schools/${schoolId}/players/${id}`,
      );
      return getResponseData(response);
    },
    create: async (schoolId: string | number, payload: Record<string, any>) => {
      if (hasFileValue(payload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/schools/${schoolId}/players`,
          rootKey: "player",
          payload,
        });
      }

      return authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/schools/${schoolId}/players`,
        rootKey: "player",
        payload,
      });
    },
    update: async (
      schoolId: string | number,
      id: string | number,
      payload: Record<string, any>,
    ) => {
      if (hasFileValue(payload)) {
        return authMultipartRequest({
          method: "patch",
          path: `${apiBaseUrl}/schools/${schoolId}/players/${id}`,
          rootKey: "player",
          payload,
        });
      }

      return authJsonRequest({
        method: "patch",
        path: `${apiBaseUrl}/schools/${schoolId}/players/${id}`,
        rootKey: "player",
        payload,
      });
    },
    remove: async (schoolId: string | number, id: string | number) => {
      const response = await authNetworkService.delete(
        `${apiBaseUrl}/schools/${schoolId}/players/${id}`,
      );
      return getResponseData(response);
    },
  },

  schoolTeamPlayers: {
    list: async (schoolTeamId: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/school_teams/${schoolTeamId}/school_team_players`,
      );
      return getResponseData(response);
    },
    create: async (schoolTeamId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/school_teams/${schoolTeamId}/school_team_players`,
        rootKey: "school_team_player",
        payload,
      }),
    remove: async (schoolTeamId: string | number, id: string | number) => {
      const response = await authNetworkService.delete(
        `${apiBaseUrl}/school_teams/${schoolTeamId}/school_team_players/${id}`,
      );
      return getResponseData(response);
    },
  },

  galleryItems: {
    listSchool: async (schoolId: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/schools/${schoolId}/gallery_items`,
      );
      return getResponseData(response);
    },
    createSchool: async (schoolId: string | number, payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/schools/${schoolId}/gallery_items`,
          rootKey: "gallery_item",
          payload: normalizedPayload,
        });
      }

      return authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/schools/${schoolId}/gallery_items`,
        rootKey: "gallery_item",
        payload: normalizedPayload,
      });
    },
    listSchoolTeam: async (schoolTeamId: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/school_teams/${schoolTeamId}/gallery_items`,
      );
      return getResponseData(response);
    },
    createSchoolTeam: async (schoolTeamId: string | number, payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/school_teams/${schoolTeamId}/gallery_items`,
          rootKey: "gallery_item",
          payload: normalizedPayload,
        });
      }

      return authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/school_teams/${schoolTeamId}/gallery_items`,
        rootKey: "gallery_item",
        payload: normalizedPayload,
      });
    },
    listPlayer: async (playerId: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/players/${playerId}/gallery_items`,
      );
      return getResponseData(response);
    },
    createPlayer: async (playerId: string | number, payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/players/${playerId}/gallery_items`,
          rootKey: "gallery_item",
          payload: normalizedPayload,
        });
      }

      return authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/players/${playerId}/gallery_items`,
        rootKey: "gallery_item",
        payload: normalizedPayload,
      });
    },
    listTeam: async (teamId: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/teams/${teamId}/gallery_items`);
      return getResponseData(response);
    },
    createTeam: async (teamId: string | number, payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/teams/${teamId}/gallery_items`,
          rootKey: "gallery_item",
          payload: normalizedPayload,
        });
      }

      return authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/teams/${teamId}/gallery_items`,
        rootKey: "gallery_item",
        payload: normalizedPayload,
      });
    },
    update: async (id: string | number, payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "patch",
          path: `${apiBaseUrl}/gallery_items/${id}`,
          rootKey: "gallery_item",
          payload: normalizedPayload,
        });
      }

      return authJsonRequest({
        method: "patch",
        path: `${apiBaseUrl}/gallery_items/${id}`,
        rootKey: "gallery_item",
        payload: normalizedPayload,
      });
    },
    remove: async (id: string | number) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/gallery_items/${id}`);
      return getResponseData(response);
    },
  },

  schoolDocuments: {
    list: async (schoolId: string | number, params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/schools/${schoolId}/documents`, {
        params,
      });
      return getResponseData(response);
    },
    get: async (id: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/school_documents/${id}`);
      return getResponseData(response);
    },
    create: async (schoolId: string | number, payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/schools/${schoolId}/documents`,
          rootKey: "school_document",
          payload: normalizedPayload,
        });
      }

      return authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/schools/${schoolId}/documents`,
        rootKey: "school_document",
        payload: normalizedPayload,
      });
    },
    update: async (id: string | number, payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "patch",
          path: `${apiBaseUrl}/school_documents/${id}`,
          rootKey: "school_document",
          payload: normalizedPayload,
        });
      }

      return authJsonRequest({
        method: "patch",
        path: `${apiBaseUrl}/school_documents/${id}`,
        rootKey: "school_document",
        payload: normalizedPayload,
      });
    },
    verify: async (id: string | number) => {
      const response = await authNetworkService.patch(
        `${apiBaseUrl}/school_documents/${id}/verify`,
      );
      return getResponseData(response);
    },
    remove: async (id: string | number) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/school_documents/${id}`);
      return getResponseData(response);
    },
  },

  schoolMatches: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/school_matches`, { params });
      return getResponseData(response);
    },
    get: async (id: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/school_matches/${id}`);
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/school_matches`,
        rootKey: "school_match",
        payload,
      }),
    update: async (id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/school_matches/${id}`,
        rootKey: "school_match",
        payload,
      }),
  },

  schoolMatchScoresheets: {
    get: async (schoolMatchId: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/school_matches/${schoolMatchId}/scoresheet`,
      );
      return getResponseData(response);
    },
    save: async (schoolMatchId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/school_matches/${schoolMatchId}/scoresheet`,
        rootKey: "school_match_scoresheet",
        payload,
      }),
    update: async (schoolMatchId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/school_matches/${schoolMatchId}/scoresheet`,
        rootKey: "school_match_scoresheet",
        payload,
      }),
  },

  schoolMatchLiveEvents: {
    list: async (schoolMatchId: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/school_matches/${schoolMatchId}/live_events`,
      );
      return getResponseData(response);
    },
    create: async (schoolMatchId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/school_matches/${schoolMatchId}/live_events`,
        rootKey: "school_match_live_event",
        payload,
      }),
    update: async (schoolMatchId: string | number, id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/school_matches/${schoolMatchId}/live_events/${id}`,
        rootKey: "school_match_live_event",
        payload,
      }),
  },

  schoolRosters: {
    list: async (schoolMatchId: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/school_matches/${schoolMatchId}/rosters`,
      );
      return getResponseData(response);
    },
    get: async (schoolMatchId: string | number, id: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/school_matches/${schoolMatchId}/rosters/${id}`,
      );
      return getResponseData(response);
    },
    create: async (schoolMatchId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/school_matches/${schoolMatchId}/rosters`,
        rootKey: "school_match_roster",
        payload,
      }),
    update: async (
      schoolMatchId: string | number,
      id: string | number,
      payload: Record<string, any>,
    ) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/school_matches/${schoolMatchId}/rosters/${id}`,
        rootKey: "school_match_roster",
        payload,
      }),
  },

  playerPerformanceRecords: {
    list: async (playerId: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/players/${playerId}/player_performance_records`,
      );
      return getResponseData(response);
    },
    create: async (playerId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/players/${playerId}/player_performance_records`,
        rootKey: "player_performance_record",
        payload,
      }),
    update: async (playerId: string | number, id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/players/${playerId}/player_performance_records/${id}`,
        rootKey: "player_performance_record",
        payload,
      }),
    remove: async (playerId: string | number, id: string | number) => {
      const response = await authNetworkService.delete(
        `${apiBaseUrl}/players/${playerId}/player_performance_records/${id}`,
      );
      return getResponseData(response);
    },
  },

  merchandise: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/merchandise_items`, { params });
      return getResponseData(response);
    },
    get: async (id: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/merchandise_items/${id}`);
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "post",
          path: `${apiBaseUrl}/merchandise_items`,
          rootKey: "merchandise_item",
          payload: normalizedPayload,
        });
      }

      const response = await authNetworkService.post(`${apiBaseUrl}/merchandise_items`, {
        merchandise_item: normalizedPayload,
      });
      return getResponseData(response);
    },
    update: async (id: string | number, payload: Record<string, any>) => {
      const normalizedPayload = compactObject(payload);

      if (hasFileValue(normalizedPayload)) {
        return authMultipartRequest({
          method: "patch",
          path: `${apiBaseUrl}/merchandise_items/${id}`,
          rootKey: "merchandise_item",
          payload: normalizedPayload,
        });
      }

      const response = await authNetworkService.patch(`${apiBaseUrl}/merchandise_items/${id}`, {
        merchandise_item: normalizedPayload,
      });
      return getResponseData(response);
    },
    remove: async (id: string | number) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/merchandise_items/${id}`);
      return getResponseData(response);
    },
  },

  news: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/news`, { params });
      return getResponseData(response);
    },
    get: async (id: string) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/news/${id}`);
      return getResponseData<NewsArticleSummary>(response);
    },
    create: async (payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/news`,
        rootKey: "news_article",
        payload: compactObject(payload),
      }),
    update: async (id: string | number, payload: Record<string, any>) => {
      return authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/news/${id}`,
        rootKey: "news_article",
        payload: compactObject(payload),
      });
    },
    remove: async (id: string | number) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/news/${id}`);
      return getResponseData(response);
    },
  },

  events: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/events`, { params });
      return getResponseData(response);
    },
    get: async (id: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/events/${id}`);
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/events`,
        rootKey: "event",
        payload: compactObject(payload),
      }),
    update: async (id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/events/${id}`,
        rootKey: "event",
        payload: compactObject(payload),
      }),
    remove: async (id: string | number) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/events/${id}`);
      return getResponseData(response);
    },
  },

  playerClasses: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/player_classes`, { params });
      return getResponseData(response);
    },
    get: async (id: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/player_classes/${id}`);
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/player_classes`,
        rootKey: "player_class",
        payload: compactObject(payload),
      }),
    update: async (id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "put",
        path: `${apiBaseUrl}/player_classes/${id}`,
        rootKey: "player_class",
        payload: compactObject(payload),
      }),
    remove: async (id: string | number) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/player_classes/${id}`);
      return getResponseData(response);
    },
  },

  requests: {
    available: async (playerId: string | number) => {
      const response = await authNetworkService.get(
        `${apiBaseUrl}/players/${playerId}/requests/available`,
      );
      return getResponseData(response);
    },
    list: async (playerId: string | number, params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/players/${playerId}/requests`, {
        params,
      });
      return getResponseData(response);
    },
    create: async (playerId: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/players/${playerId}/requests`,
        rootKey: "request",
        payload: compactObject(payload),
      }),
    fulfill: async (id: string | number) => {
      const response = await authNetworkService.patch(`${apiBaseUrl}/requests/${id}/fulfill`);
      return getResponseData(response);
    },
    cancel: async (id: string | number) => {
      const response = await authNetworkService.patch(`${apiBaseUrl}/requests/${id}/cancel`);
      return getResponseData(response);
    },
  },

  orders: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/orders`, { params });
      return getResponseData(response);
    },
    get: async (id: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/orders/${id}`);
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/orders`,
        rootKey: "order",
        payload: compactObject(payload),
      }),
    checkout: async (payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/orders/checkout`,
        rootKey: "checkout",
        payload,
      }),
    cancel: async (id: string | number) => {
      const response = await authNetworkService.patch(`${apiBaseUrl}/orders/${id}/cancel`);
      return getResponseData(response);
    },
  },

  payments: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/payments`, { params });
      return getResponseData(response);
    },
    get: async (id: string | number) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/payments/${id}`);
      return getResponseData(response);
    },
    create: async (payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/payments`,
        rootKey: "payment",
        payload: compactObject(payload),
      }),
  },

  wallets: {
    me: async () => {
      const response = await authNetworkService.get(`${apiBaseUrl}/wallets/me`);
      return getResponseData(response);
    },
    get: async (id: string | number, params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/wallets/${id}`, { params });
      return getResponseData(response);
    },
    topUp: async (id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/wallets/${id}/top_up`,
        rootKey: "wallet",
        payload: compactObject(payload),
      } as any),
    transfer: async (id: string | number, payload: Record<string, any>) =>
      authJsonRequest({
        method: "post",
        path: `${apiBaseUrl}/wallets/${id}/transfer`,
        rootKey: "wallet",
        payload: compactObject(payload),
      } as any),
  },

  walletTransactions: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/wallet_transactions`, {
        params,
      });
      return getResponseData(response);
    },
  },

  notifications: {
    list: async (params?: Record<string, any>) => {
      const response = await authNetworkService.get(`${apiBaseUrl}/notifications`, { params });
      return getResponseData(response);
    },
    read: async (id: string | number) => {
      const response = await authNetworkService.patch(`${apiBaseUrl}/notifications/${id}/read`);
      return getResponseData(response);
    },
    readAll: async () => {
      const response = await authNetworkService.patch(`${apiBaseUrl}/notifications/read_all`);
      return getResponseData(response);
    },
    unreadCount: async () => {
      const response = await authNetworkService.get(`${apiBaseUrl}/notifications/unread_count`);
      return getResponseData(response);
    },
    remove: async (id: string | number) => {
      const response = await authNetworkService.delete(`${apiBaseUrl}/notifications/${id}`);
      return getResponseData(response);
    },
  },
};

export default adminApi;
